import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {Spin} from 'antd';
import {toast} from 'react-toastify';
import {GET_FINANCIAL_STATISTICS} from '../resolvers';
import {
  Card,
  EmptyState,
  Section,
  ShareBar,
  StatCard,
} from '../../shared/DashboardUI';
import DateRangeFilter, {describeRange} from '../../shared/DateRangeFilter';
import {moneyNumber, plainNumber, seriesColor} from '../../shared/chartTheme';
import styles from '../../shared/dashboard.module.scss';

/**
 * Money in, money out, and what the platform earned.
 *
 * The old tab printed everything the endpoint returned, in the order the code
 * happened to be written: withdrawals overview, withdrawal statistics, deposits
 * overview, deposit statistics, then — before the asset-investment overview it
 * belonged to — the asset-investment statistics. Headings alternated between
 * h2, h3 and h4 with no relationship to depth, and the "statistics" blocks
 * frequently restated a figure the "overview" block above had already given
 * (one XOF withdrawal of CFA 990, shown twice).
 *
 * The rebuild reads top-down instead: what came in and went out, then the
 * revenue that produced, then the detail behind each. Statistics and overview
 * for the same subject sit together, so the repetition is visible as a summary
 * of the rows beneath it rather than as a second, unexplained answer.
 */

const STATUS_TONE = {
  SUCCESSFUL: '#0f8a6a',
  SETTLED: '#0f8a6a',
  FILLED: '#0f8a6a',
  PENDING: '#b4530a',
  OPEN: '#2f6bd4',
  PARTIALLY_FILLED: '#2f6bd4',
  FAILED: '#c62828',
  REJECTED: '#c62828',
  CANCELLED: '#8e8aa3',
};

const statusColor = status =>
  STATUS_TONE[(status || '').toUpperCase()] ?? '#8e8aa3';

const titleCase = value =>
  (value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());

const money = (symbol, amount) => `${symbol ?? ''}${moneyNumber(amount)}`;

/**
 * The successful totals for one flow, one card per currency.
 *
 * Currencies are never summed — CFA 990 and $3,218 are two facts, and adding
 * them would produce a number that is true of nothing.
 */
const CurrencyTotals = ({rows, label}) => (
  <div className={styles.autoStats}>
    {(rows ?? []).map((row, index) => (
      <StatCard
        key={`${row?.currency}-${index}`}
        label={`${row?.currency} ${label}`}
        value={moneyNumber(row?.totalAmount)}
        unit={row?.symbol}
        hint={row?.status ? titleCase(row.status) : undefined}
      />
    ))}
  </div>
);

/** One overview row: a currency and status pairing with its three figures. */
const FlowCard = ({item, fields}) => (
  <Card>
    <div className={styles.flowHead}>
      <span className={styles.flowCurrency}>{item?.currency}</span>
      {item?.status && (
        <span
          className={styles.flowBadge}
          style={{
            color: statusColor(item.status),
            background: `${statusColor(item.status)}14`,
          }}>
          {titleCase(item.status)}
        </span>
      )}
      {item?.stockExchange && (
        <span className={styles.flowTag}>{item.stockExchange}</span>
      )}
      {item?.instrumentType && (
        <span className={styles.flowTag}>{titleCase(item.instrumentType)}</span>
      )}
    </div>
    <dl className={styles.flowRows}>
      {fields.map(field => (
        <div key={field.label}>
          <dt>{field.label}</dt>
          <dd>{field.value}</dd>
        </div>
      ))}
    </dl>
  </Card>
);

/**
 * `statistics` and `overview` look like the same figures whenever a currency
 * has only one successful transaction and nothing else — which is why the old
 * screen appeared to print CFA 990 twice with no explanation. They are not the
 * same measure: the summary counts successful transfers only, the cards below
 * break out every status. Saying so is what makes the repetition legible.
 */
const TransferSection = ({title, description, group, scope, verb}) => {
  const overview = group?.overview ?? [];
  const statistics = group?.statistics ?? [];
  if (!overview.length && !statistics.length) return null;

  return (
    <Section title={title} description={description} meta={scope}>
      {statistics.length > 0 && (
        <>
          <span className={styles.subLabel}>Successful only</span>
          <CurrencyTotals rows={statistics} label={verb} />
        </>
      )}
      {overview.length > 0 && (
        <span className={styles.subLabel}>Every status, by currency</span>
      )}
      {overview.length > 0 && (
        <div className={styles.flowGrid}>
          {overview.map((item, index) => (
            <FlowCard
              key={`${item?.currency}-${item?.status}-${index}`}
              item={item}
              fields={[
                {
                  label: 'Amount',
                  value: money(item?.symbol, item?.totalAmount),
                },
                {
                  label: 'Transactions',
                  value: plainNumber(Number(item?.totalCount) || 0),
                },
                {label: 'Fees', value: money(item?.symbol, item?.totalFees)},
              ]}
            />
          ))}
        </div>
      )}
    </Section>
  );
};

const InvestmentsSection = ({group, scope}) => {
  const overview = group?.overview ?? [];
  const statistics = group?.statistics ?? [];
  if (!overview.length && !statistics.length) return null;

  return (
    <Section
      title="Asset investments"
      description="Order value by currency, exchange and instrument."
      meta={scope}>
      {statistics.length > 0 && (
        <>
          <span className={styles.subLabel}>Order value by status</span>
          <CurrencyTotals rows={statistics} label="ordered" />
        </>
      )}
      {overview.length > 0 && (
        <span className={styles.subLabel}>
          Buy and sell activity, by exchange and instrument
        </span>
      )}
      {overview.length > 0 && (
        <div className={styles.flowGrid}>
          {overview.map((item, index) => (
            <FlowCard
              key={`${item?.currency}-${item?.instrumentType}-${index}`}
              item={item}
              fields={[
                {
                  label: `Bought (${plainNumber(
                    Number(item?.totalBuyCount) || 0,
                  )})`,
                  value: money(item?.symbol, item?.totalBuyAmount),
                },
                {
                  label: 'Buy fees',
                  value: money(item?.symbol, item?.totalBuyFees),
                },
                {
                  label: `Sold (${plainNumber(
                    Number(item?.totalSellCount) || 0,
                  )})`,
                  value: money(item?.symbol, item?.totalSellAmount),
                },
                {
                  label: 'Sell fees',
                  value: money(item?.symbol, item?.totalSellFees),
                },
              ]}
            />
          ))}
        </div>
      )}
    </Section>
  );
};

/**
 * Where revenue came from, per currency.
 *
 * The share bars are the point: five figures in a column tell you the sizes but
 * not the mix, and the mix is what a revenue breakdown exists to show.
 */
const RevenueSection = ({breakdown, scope}) => {
  const rows = Array.isArray(breakdown)
    ? breakdown
    : breakdown
    ? [breakdown]
    : [];

  if (!rows.length)
    return (
      <Section title="Revenue" meta={scope}>
        <EmptyState
          title="No revenue recorded for this period"
          description="Widen the date range to include earlier activity."
        />
      </Section>
    );

  const amountOf = field =>
    typeof field === 'object' && field != null
      ? Number(field?.amount) || 0
      : Number(field) || 0;

  return (
    <Section
      title="Revenue"
      description="What the platform earned, split by source."
      meta={scope}>
      <div className={styles.split}>
        {rows.map((row, index) => {
          const symbol = row?.symbol || row?.currency || '';
          const sources = [
            {label: 'Daba Pro sales', field: row?.dabaProSales},
            {label: 'Transactional revenue', field: row?.transactionRevenue},
            {label: 'Daba Academy sales', field: row?.dabaAcademySales},
            {label: 'Simulator sales', field: row?.simulatorSales},
            {label: 'Wallet revenue', field: row?.walletRevenue},
          ].map((source, sourceIndex) => ({
            ...source,
            amount: amountOf(source.field),
            color: seriesColor(sourceIndex),
          }));

          const declared = amountOf(row?.totalRevenue);
          const summed = sources.reduce((sum, item) => sum + item.amount, 0);
          const total = declared || summed;

          return (
            <Card key={`revenue-${row?.currency || index}`}>
              <div className={styles.sectionHead}>
                <div>
                  <h3>{row?.currency || 'Revenue'}</h3>
                  <p>Total earned in this currency</p>
                </div>
                <span className={styles.revenueTotal}>
                  {money(symbol, total)}
                </span>
              </div>

              <div className={styles.statusList}>
                {sources.map(source => (
                  <div key={source.label} className={styles.statusRow}>
                    <div className={styles.statusTop}>
                      <span className={styles.statusLabel}>{source.label}</span>
                      <span className={styles.statusValue}>
                        {money(symbol, source.amount)}
                        {total > 0 && (
                          <span className={styles.statusShare}>
                            {Math.round((source.amount * 100) / total)}%
                          </span>
                        )}
                      </span>
                    </div>
                    <ShareBar
                      value={source.amount}
                      total={total}
                      color={source.color}
                    />
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </Section>
  );
};

export const FinancialMetricsTab = () => {
  const [range, setRange] = useState({startDate: null, endDate: null});

  const {data, loading} = useQuery(GET_FINANCIAL_STATISTICS, {
    variables: {filters: range},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: () => toast.error('Could not load financial metrics'),
  });

  const metrics = data?.getFinancialStatistics?.data;
  const scope = describeRange(range);

  const nothing =
    !loading &&
    !metrics?.deposits?.overview?.length &&
    !metrics?.withdrawals?.overview?.length &&
    !metrics?.assetInvestments?.overview?.length &&
    !metrics?.revenueBreakdown;

  return (
    <div className={styles.page} style={{padding: 0}}>
      <DateRangeFilter value={range} onChange={setRange} loading={loading} />

      {loading ? (
        <div className={styles.loading}>
          <Spin />
          <span>Loading financial metrics…</span>
        </div>
      ) : nothing ? (
        <EmptyState
          title="No financial activity in this period"
          description="Nothing was deposited, withdrawn or invested in the selected range."
        />
      ) : (
        <>
          <RevenueSection breakdown={metrics?.revenueBreakdown} scope={scope} />
          <TransferSection
            title="Deposits"
            description="Money coming into wallets."
            group={metrics?.deposits}
            scope={scope}
            verb="deposited"
          />
          <TransferSection
            title="Withdrawals"
            description="Money leaving wallets."
            group={metrics?.withdrawals}
            scope={scope}
            verb="withdrawn"
          />
          <InvestmentsSection group={metrics?.assetInvestments} scope={scope} />
        </>
      )}
    </div>
  );
};

export default FinancialMetricsTab;
